// admin-instructor.login.component.ts
import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from '../shared/common.service';
import { ToastService } from '../shared/toast.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SocketService } from '../services/socket.service';
import { InactivityService } from '../services/inactivity.service';

@Component({
  selector: 'app-admin-instructor-login',
  templateUrl: './admin-instructor.login.component.html',
  styleUrls: ['./admin-instructor.login.component.scss']
})
export class AdminInstuctorLoginComponent implements OnDestroy {

  email        = '';
  password     = '';
  role         = 'admin';
  rememberMe   = false;
  showPassword = false;
  submitted    = false;

  // ── OTP state ──────────────────────────────────────────────
  showOtpModal   = false;
  otpDigits      = ['', '', '', '', '', ''];
  otpError       = '';
  otpShake       = false;
  isOtpSending   = false;
  isOtpVerifying = false;

  resendCooldown  = 60;
  resendRemaining = 0;
  private resendTimer: any = null;

  videoUrl!: SafeResourceUrl;

  constructor(
    private cs:           CommonService,
    private router:       Router,
    private route:        ActivatedRoute,
    private toast:        ToastService,
    private sanitizer:    DomSanitizer,
    private socketService: SocketService,
    private inactivity: InactivityService
  ) {}

  ngOnInit(): void {
    this.role = 'admin';
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://www.youtube.com/embed/rL4zzMLGBCs'
    );
    this.route.queryParams.subscribe(p => {
      if (p['email']) this.email = p['email'];
      const qpRole = (p['role'] || '').toLowerCase();
      if (qpRole === 'admin' || qpRole === 'instructor') this.role = qpRole;
    });
  }

  ngOnDestroy(): void {
    if (this.resendTimer) clearInterval(this.resendTimer);
  }

  setRole(r: 'admin' | 'instructor'): void {
    this.role = r;
  }

  // ── STEP 1: Submit form → request OTP ──────────────────────
  onLogin(): void {
    this.submitted = true;

    const safeRole = this.role.toLowerCase();
    if (safeRole !== 'admin' && safeRole !== 'instructor') {
      this.toast.showError('Please select Admin or Instructor to continue.');
      return;
    }
    if (!this.email || !this.password) {
      this.toast.showError('Email and password are required.');
      return;
    }

    this.isOtpSending = true;

    this.cs.requestLoginOtp({ email: this.email, password: this.password, role: safeRole }).subscribe({
      next: (res: any) => {
        this.isOtpSending = false;
        this.otpDigits    = ['', '', '', '', '', ''];
        this.otpError     = '';
        this.showOtpModal = true;
        this.startResendCooldown();
        // Clear DOM inputs in case modal was previously open
        setTimeout(() => {
          for (let i = 0; i < 6; i++) {
            const el = this.getOtpInput(i);
            if (el) el.value = '';
          }
          this.getOtpInput(0)?.focus();
        }, 50);
        this.toast.showSuccess(res?.message || 'Verification code sent to your email.');
      },
      error: (err: any) => {
        this.isOtpSending = false;
        this.toast.showError(err?.error?.message || 'Login failed. Please try again.');
      }
    });
  }

  // ── STEP 2: Verify OTP → complete login ────────────────────
  verifyOtpAndLogin(): void {
    const otpCode = this.otpDigits.join('').trim();
    if (!/^\d{6}$/.test(otpCode)) {
      this.triggerShake('Please enter the complete 6-digit code.');
      return;
    }

    this.isOtpVerifying = true;
    this.otpError       = '';

    this.cs.login({
      email:    this.email,
      password: this.password,
      role:     this.role.toLowerCase(),
      otpCode
    }).subscribe({
      next: (res: any) => {
        this.isOtpVerifying = false;
        this.showOtpModal   = false;

        this.toast.showSuccess(res?.message || 'Login successful!');

        if (res?.token) {
          localStorage.setItem('authToken', res.token);
          localStorage.setItem('email', res.user?.email || this.email);
        }
        if (res?.user?._id) {
          localStorage.setItem('userId', res.user._id);
          const name = `${res.user?.firstName || ''} ${res.user?.lastName || ''}`.trim();
          localStorage.setItem('name', name);
          this.socketService.join(res.user._id);
        }
        this.socketService.refreshAuthToken();
        this.inactivity.start(); // ← ADD THIS LINE

        const role = (res?.user?.role || 'student').toLowerCase();
        localStorage.setItem('role', role);

        const redirectMap: Record<string, string> = {
          admin:      '/admin/dashboard',
          instructor: '/instructor/dashboard',
          student:    '/student/dashboard'
        };
        setTimeout(() => this.router.navigate([redirectMap[role] || '/']), 200);
      },
      error: (err: any) => {
        this.isOtpVerifying = false;
        this.triggerShake(err?.error?.message || 'Invalid code. Please try again.');
      }
    });
  }

  // ── Resend ─────────────────────────────────────────────────
  resendOtp(): void {
    if (this.resendRemaining > 0 || this.isOtpSending) return;

    this.isOtpSending = true;
    this.startResendCooldown();

    this.cs.requestLoginOtp({
      email:    this.email,
      password: this.password,
      role:     this.role.toLowerCase()
    }).subscribe({
      next: (res: any) => {
        this.isOtpSending = false;
        this.otpDigits    = ['', '', '', '', '', ''];
        this.otpError     = '';
        // Clear DOM inputs
        for (let i = 0; i < 6; i++) {
          const el = this.getOtpInput(i);
          if (el) el.value = '';
        }
        this.getOtpInput(0)?.focus();
        this.toast.showSuccess(res?.message || 'New code sent.');
      },
      error: (err: any) => {
        this.isOtpSending = false;
        this.toast.showError(err?.error?.message || 'Failed to resend code.');
      }
    });
  }

  // ── Close modal ────────────────────────────────────────────
  closeOtpModal(): void {
    if (this.isOtpVerifying) return;
    this.showOtpModal = false;
    this.otpError     = '';
    this.otpDigits    = ['', '', '', '', '', ''];
    if (this.resendTimer) { clearInterval(this.resendTimer); this.resendTimer = null; }
    this.resendRemaining = 0;
  }

  // ── Helper: get OTP input by index ─────────────────────────
  private getOtpInput(index: number): HTMLInputElement | null {
    return document.getElementById(`otp-${index}`) as HTMLInputElement | null;
  }

  // ── OTP keydown — handles everything ──────────────────────
  onOtpKeyDown(e: KeyboardEvent, index: number): void {
    const input = e.target as HTMLInputElement;

    // Digit typed
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      input.value = e.key;
      this.otpDigits[index] = e.key;
      if (index < 5) this.getOtpInput(index + 1)?.focus();
      return;
    }

    // Backspace
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (input.value) {
        input.value = '';
        this.otpDigits[index] = '';
      } else if (index > 0) {
        this.otpDigits[index - 1] = '';
        const prev = this.getOtpInput(index - 1);
        if (prev) { prev.value = ''; prev.focus(); }
      }
      return;
    }

    // Arrow keys
    if (e.key === 'ArrowLeft')  { e.preventDefault(); this.getOtpInput(index - 1)?.focus(); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); this.getOtpInput(index + 1)?.focus(); return; }

    // Block non-digit printable keys
    if (e.key.length === 1) e.preventDefault();
  }

  // ── OTP paste ──────────────────────────────────────────────
  onOtpPaste(e: ClipboardEvent): void {
    e.preventDefault();
    const text = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;

    this.otpDigits = ['', '', '', '', '', ''];
    text.split('').forEach((ch, i) => {
      this.otpDigits[i] = ch;
      const el = this.getOtpInput(i);
      if (el) el.value = ch;
    });

    this.getOtpInput(Math.min(text.length - 1, 5))?.focus();
  }

  // ── Helpers ────────────────────────────────────────────────
  private triggerShake(msg: string): void {
    this.otpError = msg;
    this.otpShake = true;
    setTimeout(() => (this.otpShake = false), 450);
  }

  private startResendCooldown(): void {
    this.resendRemaining = this.resendCooldown;
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.resendTimer = setInterval(() => {
      this.resendRemaining--;
      if (this.resendRemaining <= 0) {
        clearInterval(this.resendTimer);
        this.resendTimer     = null;
        this.resendRemaining = 0;
      }
    }, 1000);
  }

  togglePasswordVisibility(field: string): void {
    if (field === 'password') this.showPassword = !this.showPassword;
  }
}